import React from 'react';
import { Text, Icon } from '@ui-library';
import { IconName } from '@ui-library/Icon/Icon.type';
import { DevProject } from '../../types/chat';
import { IfHasPermission } from '../IfHasPermission';
import { Permissions } from '../../constants';
import { MCPServer } from '../../services/mcpApi';
import { StatusBadge } from './StatusBadge';
import { EnableToggle } from './EnableToggle';

interface ServerListViewProps {
  loading: boolean;
  error: string | null;
  servers: MCPServer[];
  togglingId: string | null;
  deletingId: string | null;
  actionError: string | null;
  isBuilding: boolean;
  onRetry: () => void;
  onToggle: (server: MCPServer) => void;
  onDelete: (server: MCPServer) => void;
  onOpenDetails: (server: MCPServer) => void;
  onAddCustom: () => void;
  project: DevProject | null;
}

/** The server list: add button, per-server cards with status/toggle/delete. */
export const ServerListView: React.FC<ServerListViewProps> = ({
  loading,
  error,
  servers,
  togglingId,
  deletingId,
  actionError,
  isBuilding,
  onRetry,
  onToggle,
  onDelete,
  onOpenDetails,
  onAddCustom,
  project,
}) => {
  if (loading) {
    return (
      <div className="flex flex-col gap-bg-v2-lg w-full">
        {[0, 1].map((index) => (
          <div
            key={index}
            className="h-[76px] skeleton-shimmer rounded-bg-v2-md w-full animate-fade-in-up opacity-0"
            style={{ animationDelay: `${index * 0.08}s`, animationFillMode: 'forwards' }}
          />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-bg-v2-8xl gap-bg-v2-lg">
        <Icon name={IconName.Alert} size="xxl" className="text-text-error-primary" inheritColor />
        <Text variant="text-md" weight="semibold" color="primary">
          Error Loading MCP Servers
        </Text>
        <Text variant="text-sm" color="secondary" align="center">
          {error}
        </Text>
        <button
          onClick={onRetry}
          className="mt-bg-v2-md px-bg-v2-xl py-bg-v2-md bg-bg-brand-solid text-text-primary-on-brand rounded-bg-v2-md hover:bg-bg-brand-solid-hover transition-colors"
        >
          <Text variant="text-sm" weight="medium" color="on-brand">
            Try Again
          </Text>
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-bg-v2-lg w-full">
      <IfHasPermission permissions={project?.permissions} requires={Permissions.UpdateProjectPermission}>
        <button
          onClick={onAddCustom}
          className="w-full flex items-center justify-center gap-bg-v2-xs px-bg-v2-lg py-bg-v2-md bg-bg-brand-solid text-text-primary-on-brand rounded-bg-v2-md hover:bg-bg-brand-solid-hover transition-colors"
        >
          <Icon name={IconName.Plus} size="sm" className="text-text-primary-on-brand" inheritColor />
          <Text variant="text-sm" weight="medium" color="on-brand">
            Add MCP server
          </Text>
        </button>
      </IfHasPermission>

      {actionError && (
        <div className="flex items-center gap-bg-v2-xs p-bg-v2-md bg-bg-error-primary border border-border-error-subtle rounded-bg-v2-sm">
          <Icon name={IconName.Alert} size="sm" className="text-text-error-primary shrink-0" inheritColor />
          <Text variant="text-xs" color="error">
            {actionError}
          </Text>
        </div>
      )}

      {servers.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-bg-v2-8xl gap-bg-v2-lg text-center w-full">
          <Icon name={IconName.Port} size="xxl" className="text-text-quaternary" inheritColor />
          <Text variant="text-md" weight="semibold" color="primary" align="center">
            No MCP Servers
          </Text>
          <Text variant="text-sm" color="secondary" align="center" className="max-w-[300px]">
            Add a remote MCP server to give the Builder extra tools.
          </Text>
        </div>
      ) : (
        servers.map((server, index) => (
          <div
            key={server.id}
            onClick={() => onOpenDetails(server)}
            className="bg-bg-primary border border-border-secondary box-border flex flex-col gap-bg-v2-md p-bg-v2-lg rounded-bg-v2-md w-full cursor-pointer hover:border-border-brand transition-colors animate-fade-in-up opacity-0"
            style={{
              boxShadow:
                '0px 1px 3px 0px var(--shadow-sm-01, rgba(10, 13, 18, 0.1)), 0px 1px 2px -1px var(--shadow-sm-02, rgba(10, 13, 18, 0.1))',
              animationDelay: `${index * 0.08}s`,
              animationFillMode: 'forwards',
            }}
          >
            <div className="flex gap-bg-v2-lg items-start w-full">
              <div className="flex flex-col gap-bg-v2-xxs items-start flex-1 min-w-0">
                <div className="flex gap-bg-v2-md items-center flex-wrap">
                  <Text variant="text-sm" weight="semibold" color="primary">
                    {server.name}
                  </Text>
                  <StatusBadge status={server.status} />
                </div>
                <Text variant="text-xs" color="tertiary" className="w-full break-all font-mono">
                  {server.url}
                </Text>
              </div>
              <div className="flex items-center gap-bg-v2-md shrink-0">
                <IfHasPermission
                  permissions={project?.permissions}
                  requires={Permissions.UpdateProjectPermission}
                >
                  <EnableToggle
                    enabled={server.enabled}
                    disabled={isBuilding || togglingId === server.id || deletingId === server.id}
                    onToggle={() => onToggle(server)}
                  />
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete(server);
                    }}
                    disabled={isBuilding || deletingId === server.id}
                    className="flex items-center justify-center w-[25px] h-[25px] rounded-full hover:bg-bg-secondary-hover transition-colors disabled:opacity-50"
                    title={isBuilding ? 'Unavailable while a build is running' : 'Delete server'}
                  >
                    {deletingId === server.id ? (
                      <Icon name={IconName.Loading} size="xs" spin className="text-text-tertiary" inheritColor />
                    ) : (
                      <Icon name={IconName.Delete} size="xs" className="text-text-tertiary hover:text-text-error-primary transition-colors" inheritColor />
                    )}
                  </button>
                </IfHasPermission>
                <Icon name={IconName.ChevronRight} size="sm" className="text-text-quaternary" inheritColor />
              </div>
            </div>
            <div className="border-t border-border-secondary w-full" />
            <div className="flex gap-bg-v2-md items-center flex-wrap">
              <Text variant="text-xs" color="tertiary">
                {server.transport === 'sse' ? 'SSE' : 'Streamable HTTP'}
              </Text>
              {typeof server.tool_count === 'number' && (
                <>
                  <span className="text-text-quaternary">·</span>
                  <span className="inline-flex items-center gap-1 text-text-tertiary">
                    <Icon name={IconName.Settings} size="xs" inheritColor />
                    <Text variant="text-xs" color="tertiary">
                      {server.tool_count} tools
                    </Text>
                  </span>
                </>
              )}
              <span className="text-text-quaternary">·</span>
              <span
                title={
                  server.sharing_mode === 'individual'
                    ? 'Each user signs in with their own credentials'
                    : 'All project members share the same stored credentials'
                }
              >
                <Text variant="text-xs" color="tertiary">
                  {server.sharing_mode === 'individual' ? 'Individual' : 'Shared'}
                </Text>
              </span>
            </div>
          </div>
        ))
      )}
    </div>
  );
};

export default ServerListView;
